import React, { FC, Fragment, useEffect } from "react";
import { useSelector } from "react-redux";
import { useHistory, useLocation } from "react-router";
import { ROUTES } from "../constants";
import {
  getActiveOrganization,
  getCurrentUser,
  getOrgVersion,
} from "../redux/selectors";
import { isRouteAllowedForOrg } from "../utils/helpers";

/**
 * Binds the routed tree to the active organization.
 *
 * Pages fetch their org-scoped data in mount effects and hold it in local
 * state, so a switch that only updates Redux leaves every open page showing the
 * previous org's campaigns, lists and settings. Keying the subtree on
 * `orgVersion` unmounts it and mounts it fresh, which discards that state and
 * re-runs every fetch under the new org — the same effect as the manual refresh
 * users have been doing, without losing the session.
 *
 * It also drops the user onto the dashboard if the page they were on is not
 * permitted in the new organization.
 */
export const OrgScope: FC = ({ children }) => {
  const orgVersion = useSelector(getOrgVersion);
  const organization = useSelector(getActiveOrganization);
  const currentUser = useSelector(getCurrentUser);
  const history = useHistory();
  const { pathname } = useLocation();

  useEffect(() => {
    // Nothing to check until the profile and org list have loaded; an early
    // check would read empty roles and bounce the user off a page they can see.
    if (!organization) {
      return;
    }
    if (!isRouteAllowedForOrg(pathname, organization, currentUser)) {
      history.replace(ROUTES.DASH.INDEX);
    }
  }, [pathname, organization, currentUser, history]);

  return <Fragment key={orgVersion}>{children}</Fragment>;
};
